/**
 * CR-4766 / CR-4783 / CR-6403: the Supplier Performance filter panel's
 * customer-hierarchy scoping — which Divisions and Company Sites the user may
 * pick, and when either is locked by the top-bar view context.
 *
 * Extracted from SupplierPerformanceFilters so that file stays under the
 * 500-line limit. Every memo, guard and comment below is moved verbatim; the
 * behaviour (including the CR-6403 Division → Company Site cascade) is
 * unchanged.
 */
import { useEffect, useMemo, useRef } from 'react';
import { Site, CompanyGroup } from '../../../lib/api';
import { useView } from '../../../contexts/ViewContext';
import { usePortalMode } from '../../../hooks/usePortalMode';
import type { SupplierPerformanceFilterValues } from '../SupplierPerformanceFilters';

export interface HierarchyScope {
  scopedDivisions: CompanyGroup[];
  isDivisionLocked: boolean;
  isSiteLocked: boolean;
  viewFilteredSites: Site[];
  /** Change the division (cascading the site); `apply` sends it to the page at once. */
  handleDivisionChange: (val: string, apply?: boolean) => void;
}

export default function useHierarchyScope(
  divisions: CompanyGroup[],
  sites: Site[],
  platform: string | undefined,
  localFilters: SupplierPerformanceFilterValues,
  setLocalFilters: React.Dispatch<React.SetStateAction<SupplierPerformanceFilterValues>>,
  onApplyFilters: (filters: SupplierPerformanceFilterValues) => void,
): HierarchyScope {
  const accessibleDivisions = useMemo(() => {
    if (platform === 'HELIX') return divisions;
    const accessibleGroupIds = new Set(
      sites
        .map((s) => s.group_id)
        .filter((g): g is number => typeof g === 'number'),
    );
    if (accessibleGroupIds.size === 0) return divisions;
    return divisions.filter((d) => accessibleGroupIds.has(d.id));
  }, [divisions, sites, platform]);

  // CR-4766 round 2 (Jacq 28/05): the Company Site filter must reflect the
  // topbar view context the same way DashboardFilters does. Round 1 hid the
  // Company Site filter entirely whenever divisions existed (the
  // `scopedDivisions.length === 0 && …` guard); Jacq's bounce: reinstate it
  // and scope its options to the active view (Group → all sites under the
  // group, Division → only sites in that division, Site → only that one
  // site). Lift the Dashboard's pattern (viewFilteredSites / scopedDivisions
  // / isDivisionLocked / isSiteLocked / sync effect) verbatim so the two
  // screens stay behaviourally aligned.
  const { viewLevel, selectedCompanyId, selectedSiteId, selectedGroupId } = useView();
  const { isSupplier: portalIsSupplier } = usePortalMode();

  // CR-4766 round 4 (Jacq + Sarah 03/06): R2 added the scoped/lock logic but
  // missed two data-shape mismatches that prevented it firing in the actual
  // user flows:
  //   1. The Helix-admin branch above seeds `sites` from
  //      coreAPI.getCompanies({company_type:'customer'}), whose CompanyList
  //      shape has `group` (not `group_id`). The previous filter
  //      `s.group_id === selectedGroupId` therefore matched nothing on
  //      Helix-admin Division view → every site leaked through (Jacq's
  //      bug 1a). Mirror the Dashboard pattern: `(s.group_id ?? s.group)`.
  //   2. ViewContext sets selectedCompanyId only when isCompany is true
  //      (view-level === 'COMPANY'). Pure 'SITE' view-level populates
  //      selectedSiteId and leaves selectedCompanyId null. The previous
  //      lock check `(viewLevel === SITE/COMPANY) && !!selectedCompanyId`
  //      therefore never fired in true Site view → Division stayed
  //      editable, Company Site stayed unlocked (Jacq's bugs 1b + 2a).
  //      Fix: accept either selectedCompanyId OR selectedSiteId as the
  //      "anchor company id" in the SITE/COMPANY branches.
  const anchorCompanyId = selectedCompanyId ?? selectedSiteId ?? null;

  const viewScopedSites = useMemo(() => {
    if (portalIsSupplier) return sites;
    if ((viewLevel === 'SITE' || viewLevel === 'COMPANY') && anchorCompanyId) {
      return sites.filter((s) => s.id === anchorCompanyId);
    }
    if (viewLevel === 'GROUP' && selectedGroupId && selectedGroupId > 0) {
      const inGroup = sites.filter((s) => ((s.group_id ?? (s as { group?: number | null }).group) === selectedGroupId));
      return inGroup.length > 0 ? inGroup : sites;
    }
    return sites;
  }, [sites, viewLevel, anchorCompanyId, selectedGroupId, portalIsSupplier]);

  /** Sites belonging to a division (a CompanyGroup), matched the same way the
   *  view-context GROUP branch above matches them. */
  const sitesInDivision = (divisionId: string, list: Site[]) => (
    divisionId === 'all'
      ? list
      : list.filter((s) => String((s.group_id ?? (s as { group?: number | null }).group) ?? '') === divisionId)
  );

  // CR-6403: the acceptance criteria require "selecting a Division restricts
  // the Site dropdown to sites within that division". That never happened —
  // the site list keyed off the top-bar view context only and ignored the
  // Division *filter*, so picking Aerospace still listed all six sites.
  // Layered on top of the view scope so the top-bar behaviour is unchanged.
  const viewFilteredSites = useMemo(
    () => sitesInDivision(localFilters.division, viewScopedSites),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [viewScopedSites, localFilters.division],
  );

  // Changing Division must also drop a Company Site selection that no longer
  // belongs to it, otherwise the two filters can contradict each other
  // (Division=Aerospace + Site=Stuttgart) and the query returns nothing.
  const handleDivisionChange = (val: string, apply = false) => {
    const next = val || 'all';
    const allowed = sitesInDivision(next, viewScopedSites);
    const siteStillValid = localFilters.site === 'all' || allowed.some((s) => String(s.id) === localFilters.site);
    const nextFilters = { ...localFilters, division: next, site: siteStillValid ? localFilters.site : 'all' };
    setLocalFilters(nextFilters);
    // CR-7264 rework: clearing with the x is a clear, not a pending edit.
    if (apply) onApplyFilters(nextFilters);
  };

  const scopedDivisions = useMemo(() => {
    if (portalIsSupplier) return accessibleDivisions;
    if (viewLevel === 'GROUP' && selectedGroupId && selectedGroupId > 0) {
      const match = accessibleDivisions.filter((d) => d.id === selectedGroupId);
      return match.length > 0 ? match : accessibleDivisions;
    }
    if ((viewLevel === 'SITE' || viewLevel === 'COMPANY') && anchorCompanyId) {
      const site = sites.find((s) => s.id === anchorCompanyId);
      const parentGroupId = site?.group_id ?? (site as { group?: number | null } | undefined)?.group;
      if (parentGroupId) {
        const match = accessibleDivisions.filter((d) => d.id === parentGroupId);
        if (match.length > 0) return match;
      }
    }
    return accessibleDivisions;
  }, [accessibleDivisions, viewLevel, selectedGroupId, anchorCompanyId, sites, portalIsSupplier]);

  const isDivisionLocked = !portalIsSupplier && scopedDivisions.length === 1
    && ((viewLevel === 'GROUP' && !!selectedGroupId && selectedGroupId > 0)
      || ((viewLevel === 'SITE' || viewLevel === 'COMPANY') && !!anchorCompanyId));
  const isSiteLocked = !portalIsSupplier
    && (viewLevel === 'SITE' || viewLevel === 'COMPANY')
    && !!anchorCompanyId;
  const lockedDivisionValue = isDivisionLocked ? String(scopedDivisions[0].id) : 'all';
  const lockedSiteValue = isSiteLocked && anchorCompanyId ? String(anchorCompanyId) : 'all';

  // Sync filter values to the scope lock — prevents filters.division / filters.site
  // from lingering with a stale value from a previous scope. Only clear on the
  // locked → unlocked transition (tracked via refs) so a user's manual pick at
  // an unlocked level survives until the next scope change. Same shape as the
  // CR-4577 round 3 logic in DashboardFilters.
  const prevSiteLockedRef = useRef(isSiteLocked);
  const prevDivisionLockedRef = useRef(isDivisionLocked);
  useEffect(() => {
    const updates: Partial<SupplierPerformanceFilterValues> = {};
    const wasDivisionLocked = prevDivisionLockedRef.current;
    const wasSiteLocked = prevSiteLockedRef.current;
    prevDivisionLockedRef.current = isDivisionLocked;
    prevSiteLockedRef.current = isSiteLocked;

    if (isDivisionLocked) {
      if (localFilters.division !== lockedDivisionValue) updates.division = lockedDivisionValue;
    } else if (wasDivisionLocked && localFilters.division !== 'all') {
      updates.division = 'all';
    }
    if (isSiteLocked) {
      if (localFilters.site !== lockedSiteValue) updates.site = lockedSiteValue;
    } else if (wasSiteLocked && localFilters.site !== 'all') {
      updates.site = 'all';
    }
    if (Object.keys(updates).length > 0) {
      const next = { ...localFilters, ...updates };
      setLocalFilters(next);
      onApplyFilters(next);
    }
    // localFilters / onApplyFilters intentionally excluded — including
    // them re-runs the sync on every Apply, causing a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDivisionLocked, isSiteLocked, lockedDivisionValue, lockedSiteValue]);

  return { scopedDivisions, isDivisionLocked, isSiteLocked, viewFilteredSites, handleDivisionChange };
}
